// /stacks/traffic/dev/frontend/src/features/sightings/FilterBar.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import DaypartChips from "../stats/DaypartChips";
import CameraChips from "../stats/CameraChips";
import PlateSearch from "./PlateSearch";
import { fmtAPIDate } from "../../lib/urlState";
import { fetchSightings } from "../../lib/sightingsApi"; // alias/plate exact probe

const ui = {
  row: { display: "flex", alignItems: "center", gap: 12, rowGap: 12, flexWrap: "wrap" },
  section: { marginTop: 12 },
  label: { opacity: 0.8, minWidth: 34 },
  input: {
    height: 32,
    background: "#0d1420",
    border: "1px solid #1f2a39",
    color: "#dbe7ff",
    borderRadius: 6,
    padding: "0 10px",
  },
  btn: {
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    background: "#14304f",
    border: "1px solid #2a4d77",
    color: "#dbe7ff",
    cursor: "pointer",
  },
  group: { display: "flex", alignItems: "center", gap: 8 },
};

// Map CameraChips keys (from Stats) -> query keys
const normalizeCamKey = (k) => {
  const s = String(k || "").toLowerCase();
  if (s === "cam41" || s === "41") return "41";
  if (s === "chel" || s === "cheltenham") return "cheltenham";
  if (s === "pool") return "pool";
  return s;
};

export default function FilterBar({ value, onChange }) {
  // Keep datetime-local controls in sync with external filter changes
  const [local, setLocal] = useState({
    from: value.start?.replace(" ", "T").slice(0, 16) || "",
    to:   value.end?.replace(" ", "T").slice(0, 16) || "",
  });
  useEffect(() => {
    setLocal({
      from: value.start?.replace(" ", "T").slice(0, 16) || "",
      to:   value.end?.replace(" ", "T").slice(0, 16) || "",
    });
  }, [value.start, value.end]);

  // Prevent DaypartChips’ setSunMode(null) from racing a setBucket
  const skipNextSunNull = useRef(false);

  // Daypart selection: set bucket & clear sunlight in one update
  const setBucket = (bucket) => {
    skipNextSunNull.current = true; // ignore the next sunMode(null)
    onChange({ buckets: bucket ? [bucket] : [], is_night: null, offset: 0 });
  };

  // Sunlight selection
  const setSunMode = (flag /* null|boolean */) => {
    if (flag === true || flag === false) {
      onChange({ is_night: !!flag, buckets: [], offset: 0 });
      return;
    }
    if (skipNextSunNull.current) {
      skipNextSunNull.current = false;
      return; // ignore the redundant null after a bucket click
    }
    onChange({ is_night: null, offset: 0 });
  };

  // Camera chips (normalize incoming keys like cam41/chel/pool)
  const cameraSetters = useMemo(
    () => ({
      toggle: (rawKey) => {
        const key = normalizeCamKey(rawKey);
        const set = new Set(value.cameras || []);
        set.has(key) ? set.delete(key) : set.add(key);
        onChange({ cameras: Array.from(set), offset: 0 });
      },
      showAll: () =>
        onChange({ cameras: ["41", "cheltenham", "pool"], offset: 0 }),
    }),
    [value.cameras, onChange]
  );

  // ----------------------------- Plate candidates -----------------------------
  // 1) Probe exact (alias/plate) with current filters; if any hit, auto-exact.
  // 2) Else fetch partial matches (up to 24 A→Z) for the candidate chips.
  const findPlateCandidates = async (partial) => {
    const q = String(partial || "").trim();
    if (!q) return [];

    // "=ALIAS" shortcut → apply exact immediately
    if (q.startsWith("=") && q.length > 1) return [q.slice(1)];

    try {
      const probe = await fetchSightings({
        ...value,
        plate: q,
        exact: true,
        limit: 1,
        offset: 0,
      });
      if ((probe?.total ?? 0) > 0) return [q];
    } catch {
      /* fall back to partial */
    }

    const p = new URLSearchParams();
    if (value.start) p.set("start", value.start);
    if (value.end) p.set("end", value.end);
    if (Array.isArray(value.cameras) && value.cameras.length) {
      p.set("cameras", value.cameras.join(","));
    }
    if (value.is_night === true || value.is_night === false) {
      p.set("is_night", value.is_night ? "1" : "0");
    } else if (Array.isArray(value.buckets) && value.buckets.length) {
      p.set("buckets", value.buckets.join(","));
    }
    p.set("plate", q);
    p.set("limit", "250");
    p.set("offset", "0");
    p.set("order", "desc");

    try {
      const res = await fetch(`/api/sightings?${p.toString()}`, {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const rows = Array.isArray(json?.rows) ? json.rows : [];
      const unique = Array.from(
        new Set(rows.map((r) => r?.plate).filter((s) => typeof s === "string" && s.trim()))
      );
      return unique.sort((a, b) => a.localeCompare(b)).slice(0, 24);
    } catch {
      return [];
    }
  };
  // ---------------------------------------------------------------------------

  const quick = {
    today: () => {
      const n = new Date();
      const s = new Date(n); s.setHours(0, 0, 0, 0);
      const e = new Date(n); e.setHours(24, 0, 0, 0);
      onChange({ start: fmtAPIDate(s), end: fmtAPIDate(e), offset: 0 });
    },
    thisWeek: () => {
      const n = new Date();
      const s = new Date(n); s.setHours(0, 0, 0, 0); s.setDate(s.getDate() - s.getDay());
      const e = new Date(s); e.setDate(e.getDate() + 7);
      onChange({ start: fmtAPIDate(s), end: fmtAPIDate(e), offset: 0 });
    },
    lastWeek: () => {
      const n = new Date();
      const s = new Date(n); s.setHours(0, 0, 0, 0); s.setDate(s.getDate() - s.getDay() - 7);
      const e = new Date(s); e.setDate(e.getDate() + 7);
      onChange({ start: fmtAPIDate(s), end: fmtAPIDate(e), offset: 0 });
    },
    thisMonth: () => {
      const n = new Date();
      const s = new Date(n.getFullYear(), n.getMonth(), 1, 0, 0, 0, 0);
      const e = new Date(n.getFullYear(), n.getMonth() + 1, 1, 0, 0, 0, 0);
      onChange({ start: fmtAPIDate(s), end: fmtAPIDate(e), offset: 0 });
    },
    lastMonth: () => {
      const n = new Date();
      const s = new Date(n.getFullYear(), n.getMonth() - 1, 1, 0, 0, 0, 0);
      const e = new Date(n.getFullYear(), n.getMonth(), 1, 0, 0, 0, 0);
      onChange({ start: fmtAPIDate(s), end: fmtAPIDate(e), offset: 0 });
    },
    thisYear: () => {
      const n = new Date();
      const s = new Date(n.getFullYear(), 0, 1);
      const e = new Date(n.getFullYear() + 1, 0, 1);
      onChange({ start: fmtAPIDate(s), end: fmtAPIDate(e), offset: 0 });
    },
    lastYear: () => {
      const n = new Date();
      const s = new Date(n.getFullYear() - 1, 0, 1);
      const e = new Date(n.getFullYear(), 0, 1);
      onChange({ start: fmtAPIDate(s), end: fmtAPIDate(e), offset: 0 });
    },
  };

  const applyCustom = () => {
    if (!local.from || !local.to) return;
    onChange({
      start: local.from.replace("T", " ") + ":00",
      end:   local.to.replace("T", " ") + ":00",
      offset: 0,
    });
  };

  return (
    <div className="card" style={{ padding: 12 }}>
      {/* Daypart & Sunlight chips */}
      <div style={ui.row}>
        <DaypartChips
          bucket={value.buckets?.[0] || ""}
          setBucket={setBucket}
          sunMode={value.is_night === true ? 1 : value.is_night === false ? 0 : null}
          setSunMode={(m) => setSunMode(m === null ? null : !!m)}
        />
      </div>

      {/* Quick range buttons */}
      <div style={{ ...ui.row, ...ui.section }}>
        <button style={ui.btn} onClick={quick.today}>Today</button>
        <button style={ui.btn} onClick={quick.thisWeek}>This Week</button>
        <button style={ui.btn} onClick={quick.lastWeek}>Last Week</button>
        <button style={ui.btn} onClick={quick.thisMonth}>This Month</button>
        <button style={ui.btn} onClick={quick.lastMonth}>Last Month</button>
        <button style={ui.btn} onClick={quick.thisYear}>This Year</button>
        <button style={ui.btn} onClick={quick.lastYear}>Last Year</button>
      </div>

      {/* Custom range */}
      <div style={{ ...ui.row, ...ui.section }}>
        <div style={ui.group}>
          <span style={ui.label}>From</span>
          <input
            type="datetime-local"
            value={local.from}
            onChange={(e) => setLocal((p) => ({ ...p, from: e.target.value }))}
            style={ui.input}
          />
        </div>
        <div style={ui.group}>
          <span style={ui.label}>To</span>
          <input
            type="datetime-local"
            value={local.to}
            onChange={(e) => setLocal((p) => ({ ...p, to: e.target.value }))}
            style={ui.input}
          />
        </div>
        <button style={ui.btn} onClick={applyCustom}>Apply</button>
      </div>

      {/* Cameras */}
      <div style={{ ...ui.row, ...ui.section }}>
        <CameraChips
          visible={{
            cam41: value.cameras?.includes("41"),
            chel:  value.cameras?.includes("cheltenham"),
            pool:  value.cameras?.includes("pool"),
          }}
          toggle={cameraSetters.toggle}
          showAll={cameraSetters.showAll}
        />
      </div>

      {/* Plate search + order + limit */}
      <div style={{ ...ui.row, ...ui.section }}>
        <PlateSearch
          value={value.plate ?? ""}
          findCandidates={findPlateCandidates}
          onApply={({ plate, exact }) => onChange({ plate, exact: !!exact, offset: 0 })}
          onClear={() => onChange({ plate: "", exact: false, offset: 0 })}
        />

        <span style={{ marginLeft: 10, opacity: 0.8 }}>Order</span>
        <select
          value={value.order === "asc" ? "asc" : "desc"}
          onChange={(e) =>
            onChange({ order: e.target.value === "asc" ? "asc" : "desc", offset: 0 })
          }
          style={{ ...ui.input, height: 32 }}
        >
          <option value="desc">Newest first</option>
          <option value="asc">Oldest first</option>
        </select>

        <span style={{ marginLeft: 10, opacity: 0.8 }}>Limit</span>
        <input
          type="number"
          min={1}
          value={value.limit ?? 100}
          onChange={(e) =>
            onChange({ limit: Math.max(1, Number(e.target.value || 100)), offset: 0 })
          }
          style={{ ...ui.input, width: 90 }}
        />
      </div>
    </div>
  );
}
